/**
 * Derive "Monthly briefing" / executive-summary style metrics from client
 * operations sessionLog rows ({ date, room, type, start, end, duration, method }).
 */

function parseDurationToSeconds(duration) {
  if (duration == null || duration === '') return null;
  const s = String(duration).trim().toLowerCase();
  if (!s || s === '—' || s === '-') return null;
  let sec = 0;
  const h = s.match(/(\d+)\s*h/);
  const m = s.match(/(\d+)\s*m/);
  const ss = s.match(/(\d+)\s*s/);
  if (h) sec += parseInt(h[1], 10) * 3600;
  if (m) sec += parseInt(m[1], 10) * 60;
  if (ss) sec += parseInt(ss[1], 10);
  if (!h && !m && !ss) {
    const n = parseFloat(s);
    if (!Number.isNaN(n)) return Math.round(n * 60);
  }
  return sec > 0 ? sec : null;
}

function sessionInstant(row) {
  const d = row.date;
  const t = row.start;
  if (!d) return null;
  if (t && /^\d{1,2}:\d{2}/.test(String(t).trim())) {
    const iso = `${String(d).slice(0, 10)}T${String(t).trim()}`;
    const x = new Date(iso);
    if (!Number.isNaN(x.getTime())) return x;
  }
  const x = new Date(d);
  return Number.isNaN(x.getTime()) ? null : x;
}

function isNightLocal(dt) {
  if (!dt) return false;
  const h = dt.getHours();
  return h >= 19 || h < 7;
}

function formatDateEnCA(d) {
  if (!d || Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

function median(nums) {
  const a = nums.filter((n) => n != null && !Number.isNaN(n)).sort((x, y) => x - y);
  if (!a.length) return null;
  const mid = Math.floor(a.length / 2);
  return a.length % 2 ? a[mid] : (a[mid - 1] + a[mid]) / 2;
}

/** @param {Array<Record<string, unknown>>} sessions @param {{ windowDays?: number }} opts */
export function buildBriefingBulletParts(sessions, opts = {}) {
  const { windowDays = 90 } = opts;
  const list = Array.isArray(sessions) ? sessions : [];

  if (list.length === 0) {
    return { bullets: [], hasData: false };
  }

  const instants = list.map(sessionInstant).filter(Boolean);
  const minMs = instants.length ? Math.min(...instants.map((d) => d.getTime())) : null;
  const maxMs = instants.length ? Math.max(...instants.map((d) => d.getTime())) : null;
  const fromD = minMs != null ? new Date(minMs) : null;
  const toD = maxMs != null ? new Date(maxMs) : null;

  const dayKeys = new Set();
  list.forEach((row) => {
    const ins = sessionInstant(row);
    if (ins) dayKeys.add(formatDateEnCA(ins));
  });
  const activeDays = dayKeys.size;
  const totalCycles = list.length;

  const uvcSecs = [];
  list.forEach((row) => {
    const sec = parseDurationToSeconds(row.duration);
    if (sec != null) uvcSecs.push(sec);
  });
  const totalUvcHours = uvcSecs.reduce((a, b) => a + b, 0) / 3600;

  let nightCount = 0;
  list.forEach((row) => {
    if (isNightLocal(sessionInstant(row))) nightCount += 1;
  });
  const nightPct = totalCycles > 0 ? Math.round((nightCount / totalCycles) * 1000) / 10 : 0;

  const perDayCounts = new Map();
  const perDayHours = new Map();
  list.forEach((row, idx) => {
    const ins = sessionInstant(row);
    const key = ins ? formatDateEnCA(ins) : `_row-${idx}`;
    perDayCounts.set(key, (perDayCounts.get(key) || 0) + 1);
    const sec = parseDurationToSeconds(row.duration);
    if (sec != null) {
      perDayHours.set(key, (perDayHours.get(key) || 0) + sec / 3600);
    }
  });

  let peakCycles = 0;
  let peakCyclesDate = null;
  for (const [k, n] of perDayCounts) {
    if (n > peakCycles) {
      peakCycles = n;
      peakCyclesDate = k;
    }
  }

  let peakUvH = 0;
  let peakUvDate = null;
  for (const [k, h] of perDayHours) {
    if (h > peakUvH) {
      peakUvH = h;
      peakUvDate = k;
    }
  }

  const peakCyclesStr =
    peakCyclesDate && peakCycles && !String(peakCyclesDate).startsWith('_')
      ? `${peakCycles} cycle${peakCycles === 1 ? '' : 's'} on ${peakCyclesDate}`
      : '—';

  const peakUvStr =
    peakUvDate && peakUvH > 0 && !String(peakUvDate).startsWith('_')
      ? `${Math.round(peakUvH * 10) / 10} UV hours on ${peakUvDate}`
      : '—';

  const medianUvcMin = median(uvcSecs.map((s) => s / 60));
  const medianUvcMinStr = medianUvcMin != null ? `${Math.round(medianUvcMin)} minutes` : '—';

  const runtimesMin = uvcSecs.map((s) => s / 60);
  const medianRuntimeMin = median(runtimesMin);
  const medianRuntimeStr = medianRuntimeMin != null ? `${Math.round(medianRuntimeMin * 10) / 10}-minute` : '—';

  const methods = {};
  list.forEach((row) => {
    const m = String(row.method || 'Unknown').trim() || 'Unknown';
    methods[m] = (methods[m] || 0) + 1;
  });
  const topMethod = Object.entries(methods).sort((a, b) => b[1] - a[1])[0]?.[0] || '—';
  const allSameMode = Object.keys(methods).length <= 1;

  const completed = list.filter((r) => /completed/i.test(String(r.type || ''))).length;
  const partial = list.filter((r) => /partial/i.test(String(r.type || ''))).length;
  const completedPct = totalCycles > 0 ? Math.round((completed / totalCycles) * 100) : 0;

  const byRoom = new Map();
  list.forEach((row) => {
    const room = String(row.room || 'Unknown').trim() || 'Unknown';
    byRoom.set(room, (byRoom.get(room) || 0) + 1);
  });
  const topRooms = [...byRoom.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3);

  const cyclesPerActiveDay = activeDays > 0 ? Math.round((totalCycles / activeDays) * 10) / 10 : 0;
  const totalUvcHoursRounded = Math.round(totalUvcHours * 10) / 10;

  const spanDays =
    minMs != null && maxMs != null
      ? Math.max(1, Math.round((maxMs - minMs) / (24 * 60 * 60 * 1000)) + 1)
      : windowDays;
  const windowStr =
    fromD && toD
      ? `${formatDateEnCA(fromD)} to ${formatDateEnCA(toD)} (${spanDays}-day span)`
      : `${windowDays}-day lookback`;

  const opportunityParts = [];
  if (partial > 0) {
    opportunityParts.push(`review root causes for ${partial} Partial session${partial === 1 ? '' : 's'}`);
  }
  if (nightPct >= 70) {
    opportunityParts.push('consider daytime turnover coverage where clinically appropriate');
  }
  const tailRoom = topRooms.length >= 2 ? topRooms[topRooms.length - 1][0] : null;
  if (tailRoom && topRooms[0] && tailRoom !== topRooms[0][0]) {
    opportunityParts.push(`compare utilisation in ${tailRoom} vs busier areas`);
  }
  if (!opportunityParts.length) {
    opportunityParts.push('maintain current cadence and revisit room mix if capacity changes');
  }

  const bullets = [
    {
      key: 'coverage',
      parts: [
        { bold: 'Coverage period' },
        ': ',
        { bold: windowStr },
        ' with ',
        { bold: String(activeDays) },
        ` active deployment day${activeDays === 1 ? '' : 's'}.`,
      ],
    },
    {
      key: 'volume',
      parts: [
        { bold: 'Volume' },
        ': ',
        { bold: String(totalCycles) },
        ` session${totalCycles === 1 ? '' : 's'} completed at `,
        { bold: `~${cyclesPerActiveDay}` },
        ' per active day.',
      ],
    },
    {
      key: 'uvc',
      parts: [
        { bold: 'UVC exposure' },
        ': ',
        { bold: `${totalUvcHoursRounded} hours` },
        ' total lamp-on time with median ',
        { bold: medianUvcMinStr },
        ' per session.',
      ],
    },
    {
      key: 'night',
      parts: [
        { bold: 'Night-heavy pattern' },
        ': ',
        { bold: `${nightPct}%` },
        ' of sessions started during night hours (19:00–07:00, local).',
      ],
    },
    {
      key: 'peaks',
      parts: [
        { bold: 'Peaks' },
        ': Peak ',
        { bold: peakCyclesStr },
        ' and peak ',
        { bold: peakUvStr },
        '.',
      ],
    },
    {
      key: 'mode',
      parts: allSameMode
        ? [
            { bold: 'Mode and duration' },
            ': All sessions used ',
            { bold: topMethod },
            ' mode with median ',
            { bold: medianRuntimeStr },
            ' runtime. ',
            { bold: `${completedPct}%` },
            ' completed status',
            partial > 0 ? ` (${partial} partial).` : '.',
          ]
        : [
            { bold: 'Mode and duration' },
            ': Primary mode ',
            { bold: topMethod },
            ' with median ',
            { bold: medianRuntimeStr },
            ' runtime. ',
            { bold: `${completedPct}%` },
            ' completed status',
            partial > 0 ? ` (${partial} partial).` : '.',
          ],
    },
    {
      key: 'opportunity',
      parts: [
        { bold: 'Key operational opportunity' },
        ': ',
        ...opportunityParts.map((t, i) => (i ? `; ${t}` : t)),
        '.',
      ],
    },
  ];

  return { bullets, hasData: true, fromD, toD, activeDays, totalCycles };
}

export function buildFallbackBriefingParts(kpis, coverageAreas) {
  const sessions = kpis?.sessions;
  const hours = kpis?.hoursThisMonth;
  const rooms = kpis?.roomsCovered;
  const bullets = [];

  const kpiParts = [{ bold: 'This period (dashboard KPIs)' }, ': '];
  if (hours != null) {
    kpiParts.push({ bold: `${hours}h` }, ' UVC this month');
    if (sessions != null || rooms != null) kpiParts.push(', ');
  }
  if (sessions != null) {
    kpiParts.push({ bold: String(sessions) }, ' sessions');
    if (rooms != null) kpiParts.push(', ');
  }
  if (rooms != null) {
    kpiParts.push({ bold: String(rooms) }, ' rooms covered');
  }
  if (kpiParts.length <= 2) {
    kpiParts.push('Use Operations (90-day range) or the full report for session-level history.');
  } else {
    kpiParts.push(' — tie these to subscription tier and staffing.');
  }
  bullets.push({ key: 'kpi', parts: kpiParts });

  if (Array.isArray(coverageAreas) && coverageAreas.length) {
    const sorted = [...coverageAreas].sort((a, b) => (b.value || 0) - (a.value || 0)).slice(0, 3);
    const str = sorted.map((a) => `${a.name} (${a.value}%)`).join(', ');
    bullets.push({
      key: 'cov',
      parts: [{ bold: 'Coverage by area' }, ': ', { bold: str }, '.'],
    });
  }

  bullets.push({
    key: 'hint',
    parts: [
      'Open ',
      { bold: 'Operations' },
      ' with a 90-day date range for session-level history, or use ',
      { bold: 'Read full report' },
      ' when available for a detailed executive summary.',
    ],
  });

  return { bullets, hasData: false };
}
